import type { LinkProps } from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import { InfoTip } from "@ui/components/info-tip";
import { LinkButton } from "@ui/components/link-button";
import { Spinner } from "@ui/components/spinner";
import { cn } from "@ui/lib";
import { ArrowLeftIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useSectionHeading } from "./heading-level";

/*
 * Page patterns. Every admin screen is built from these so headers, sections,
 * stats, settings rows and save bars look and behave the same everywhere.
 * See DESIGN.md § Page anatomy.
 */

/** The raised surface panels, tables and card lists sit on (DESIGN.md § Shape and surfaces). */
export const SURFACE =
	"overflow-hidden rounded-3xl bg-card ring-1 ring-foreground/6 dark:ring-foreground/10";

const PAGE_WIDTH = {
	/** Forms and settings: one readable column. */
	narrow: "max-w-3xl",
	/** Most screens. */
	default: "max-w-6xl",
	/** Tables and dashboards that earn the width. */
	wide: "max-w-[96rem]",
} as const;

export function Page({
	width = "default",
	className,
	children,
}: {
	width?: keyof typeof PAGE_WIDTH;
	className?: string;
	children: ReactNode;
}) {
	return (
		<div
			className={cn(
				"mx-auto flex w-full flex-col gap-8 px-4 pt-6 pb-16 md:px-8 md:pt-8",
				PAGE_WIDTH[width],
				className,
			)}
		>
			{children}
		</div>
	);
}

export function PageHeader({
	title,
	description,
	info,
	actions,
	meta,
	back,
	className,
}: {
	title: ReactNode;
	/** One short line, only when the title can't speak for itself. */
	description?: ReactNode;
	/** Explanation behind an ⓘ next to the title. Prefer this to `description`. */
	info?: ReactNode;
	/** Primary actions, right-aligned; they wrap under the title on phones. */
	actions?: ReactNode;
	/** Status badges or a one-line context row under the title. */
	meta?: ReactNode;
	back?: { label: string } & Pick<LinkProps, "to" | "search" | "params">;
	className?: string;
}) {
	return (
		<header className={cn("flex flex-col gap-3", className)}>
			{back ? (
				<LinkButton
					variant="ghost"
					size="sm"
					className="-ml-2.5 w-fit text-muted-foreground"
					to={back.to}
					search={back.search}
					params={back.params}
				>
					<ArrowLeftIcon data-icon="inline-start" />
					{back.label}
				</LinkButton>
			) : null}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex min-w-0 flex-col gap-1">
					<h1 className="flex items-center gap-2 text-balance font-semibold text-2xl tracking-tight">
						{title}
						{info ? (
							<InfoTip label="About this page">{info}</InfoTip>
						) : null}
					</h1>
					{description ? (
						<p className="max-w-2xl text-pretty text-muted-foreground text-sm">
							{description}
						</p>
					) : null}
					{meta ? (
						<div className="mt-1 flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
							{meta}
						</div>
					) : null}
				</div>
				{actions ? (
					<div className="flex shrink-0 flex-wrap items-center gap-2">
						{actions}
					</div>
				) : null}
			</div>
		</header>
	);
}

export function PageSection({
	id,
	title,
	description,
	info,
	actions,
	className,
	children,
}: {
	id?: string;
	title?: ReactNode;
	description?: ReactNode;
	/** Explanation behind an ⓘ next to the title. Prefer this to `description`. */
	info?: ReactNode;
	actions?: ReactNode;
	className?: string;
	children: ReactNode;
}) {
	const Heading = useSectionHeading();
	return (
		<section
			id={id}
			className={cn("flex scroll-mt-6 flex-col gap-4", className)}
		>
			{title || actions ? (
				<div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
					<div className="flex min-w-0 flex-col gap-0.5">
						{title ? (
							<Heading className="flex items-center gap-1.5 font-semibold text-base tracking-tight">
								{title}
								{info ? <InfoTip>{info}</InfoTip> : null}
							</Heading>
						) : null}
						{description ? (
							<p className="max-w-2xl text-pretty text-muted-foreground text-sm">
								{description}
							</p>
						) : null}
					</div>
					{actions ? (
						<div className="flex items-center gap-2">{actions}</div>
					) : null}
				</div>
			) : null}
			{children}
		</section>
	);
}

/**
 * A row of key numbers in one container, split by hairlines. Not a grid of
 * cards: the numbers belong together and read left to right.
 */
export function StatStrip({
	className,
	children,
}: {
	className?: string;
	children: ReactNode;
}) {
	return (
		<dl
			className={cn(
				"grid grid-cols-2 overflow-hidden rounded-3xl bg-card ring-1 ring-foreground/6 sm:grid-cols-3 lg:grid-flow-col lg:grid-cols-none lg:auto-cols-fr dark:ring-foreground/10",
				"[&>*]:border-border/70 [&>*]:border-t [&>*]:border-l [&>*]:-mt-px [&>*]:-ml-px",
				// An odd last stat fills its row on phones instead of leaving a hole.
				"max-sm:[&>*:last-child:nth-child(odd)]:col-span-2",
				className,
			)}
		>
			{children}
		</dl>
	);
}

const STAT_TONE = {
	default: "",
	good: "text-success",
	warn: "text-warning-foreground dark:text-warning",
	bad: "text-destructive",
} as const;

export function Stat({
	label,
	value,
	detail,
	info,
	tone = "default",
	className,
}: {
	label: ReactNode;
	value: ReactNode;
	detail?: ReactNode;
	/** What the number means or how it's counted. */
	info?: ReactNode;
	tone?: keyof typeof STAT_TONE;
	className?: string;
}) {
	return (
		<div
			className={cn("flex min-w-0 flex-col gap-1 p-4 md:p-5", className)}
		>
			<dt className="flex min-w-0 items-center gap-1 text-muted-foreground text-xs">
				<span className="truncate">{label}</span>
				{info ? <InfoTip>{info}</InfoTip> : null}
			</dt>
			<dd
				className={cn(
					"font-semibold text-xl tabular tracking-tight md:text-2xl",
					STAT_TONE[tone],
				)}
			>
				{value}
			</dd>
			{detail ? (
				<dd className="truncate text-muted-foreground text-xs">
					{detail}
				</dd>
			) : null}
		</div>
	);
}

/** A titled group of settings rows inside one surface. */
export function SettingsSection({
	id,
	title,
	description,
	info,
	footer,
	tone = "default",
	className,
	children,
}: {
	id?: string;
	title: ReactNode;
	description?: ReactNode;
	/** Explanation behind an ⓘ next to the title. Prefer this to `description`. */
	info?: ReactNode;
	/** Actions for the whole section (a Save button, a hint). */
	footer?: ReactNode;
	tone?: "default" | "danger";
	className?: string;
	children: ReactNode;
}) {
	const Heading = useSectionHeading();
	return (
		<section
			id={id}
			className={cn(
				"scroll-mt-6 overflow-hidden rounded-3xl bg-card ring-1 ring-foreground/6 dark:ring-foreground/10",
				tone === "danger" &&
					"ring-destructive/25 dark:ring-destructive/30",
				className,
			)}
		>
			<div className="flex flex-col gap-1 px-5 pt-5 pb-1 md:px-6">
				<Heading className="flex items-center gap-1.5 font-semibold text-base tracking-tight">
					{title}
					{info ? <InfoTip>{info}</InfoTip> : null}
				</Heading>
				{description ? (
					<p className="max-w-2xl text-pretty text-muted-foreground text-sm">
						{description}
					</p>
				) : null}
			</div>
			<div className="flex flex-col divide-y divide-border/70 px-5 md:px-6">
				{children}
			</div>
			{footer ? (
				<div className="flex flex-wrap items-center justify-end gap-2 border-border/70 border-t bg-muted/40 px-5 py-3 md:px-6">
					{footer}
				</div>
			) : null}
		</section>
	);
}

/** Label and help on the left, the control on the right; stacked on phones. */
export function SettingsRow({
	label,
	description,
	info,
	htmlFor,
	children,
	className,
}: {
	label: ReactNode;
	description?: ReactNode;
	/** Explanation behind an ⓘ next to the title. Prefer this to `description`. */
	info?: ReactNode;
	htmlFor?: string;
	children?: ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"grid gap-3 py-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-8",
				className,
			)}
		>
			<div className="flex min-w-0 flex-col gap-1">
				<span className="flex items-center gap-1.5">
					{htmlFor ? (
						<label
							htmlFor={htmlFor}
							className="font-medium text-sm"
						>
							{label}
						</label>
					) : (
						<span className="font-medium text-sm">{label}</span>
					)}
					{info ? <InfoTip>{info}</InfoTip> : null}
				</span>
				{description ? (
					<p className="text-pretty text-muted-foreground text-sm">
						{description}
					</p>
				) : null}
			</div>
			{children ? (
				<div className="flex min-w-0 flex-col items-start gap-2 sm:items-end *:data-[slot=input]:w-full *:data-[slot=input-group]:w-full *:data-[slot=select-trigger]:w-full *:data-[slot=textarea]:w-full">
					{children}
				</div>
			) : null}
		</div>
	);
}

/**
 * Sticky bar for editors with unsaved changes. Rendered at the bottom of the
 * scrolling page so it never covers the last field.
 */
export function SaveBar({
	visible,
	message = "You have unsaved changes",
	saving,
	onSave,
	onDiscard,
	saveLabel = "Save changes",
	disabled,
}: {
	visible: boolean;
	message?: ReactNode;
	saving?: boolean;
	onSave: () => void;
	onDiscard?: () => void;
	saveLabel?: string;
	disabled?: boolean;
}) {
	// Hidden, the bar takes no height (its row collapses), so a SaveBar mid-page leaves no gap.
	return (
		<div
			aria-hidden={!visible}
			className={cn(
				"pointer-events-none sticky bottom-4 z-20 grid grid-cols-1 transition-[grid-template-rows,opacity,transform] duration-(--duration-dropdown) ease-(--ease-out-ui)",
				visible
					? "translate-y-0 grid-rows-[1fr] opacity-100"
					: "translate-y-3 grid-rows-[0fr] opacity-0",
			)}
		>
			<div className="flex min-h-0 justify-center">
				<div
					role="status"
					className={cn(
						"flex w-full max-w-xl items-center gap-3 self-start rounded-3xl bg-popover py-2 pr-2 pl-4 text-popover-foreground shadow-(--shadow-overlay) ring-1 ring-foreground/8",
						visible && "pointer-events-auto",
					)}
				>
					<span className="min-w-0 flex-1 truncate text-sm">
						{message}
					</span>
					{onDiscard ? (
						<Button
							variant="ghost"
							size="sm"
							onClick={onDiscard}
							disabled={saving}
							tabIndex={visible ? 0 : -1}
						>
							Discard
						</Button>
					) : null}
					<Button
						size="sm"
						onClick={onSave}
						disabled={saving || disabled}
						tabIndex={visible ? 0 : -1}
					>
						{saving ? <Spinner data-icon="inline-start" /> : null}
						{saveLabel}
					</Button>
				</div>
			</div>
		</div>
	);
}

/** Centered spinner for a region that has nothing to show yet. */
export function LoadingRegion({
	label = "Loading",
	className,
}: {
	label?: string;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex min-h-40 flex-1 items-center justify-center gap-2 text-muted-foreground text-sm",
				className,
			)}
		>
			<Spinner />
			<span>{label}</span>
		</div>
	);
}
